import { EventEmitter } from "@/utils/event-emitter";
import { Loader } from "@googlemaps/js-api-loader";
import { i18n } from "@/setup/i18n-setup";
import { GoogleCalendarService } from "./google-calendar-service";

const loader = new Loader({
	apiKey: "AIzaSyCvB_Jp-lDA5MaL7RYuNde-U9FDCptgJmk",
	version: "weekly",
	libraries: ["places"]
});


let googleLibraryLoadFinishedCallbacks:Function[] = [];
let googleLibraryLoadFinished = false;
export async function waitForGoogleLibraryLoadingToFinish(){
	if (googleLibraryLoadFinished) {
		return;
	}
	await new Promise(resolve=>{
		googleLibraryLoadFinishedCallbacks.push(resolve);
	});
}

function markGoogleLibAsFinishedLoading(){
	googleLibraryLoadFinished = true;
	for (let i = googleLibraryLoadFinishedCallbacks.length-1; i>=0;i--){
		googleLibraryLoadFinishedCallbacks[i]();
		googleLibraryLoadFinishedCallbacks.pop();
	}
}

loader.importLibrary("places").then((google)=>{
	console.log("Google Places API loaded");
	markGoogleLibAsFinishedLoading();
}).catch((err)=>{
	console.error("Failed to load places", err);
	markGoogleLibAsFinishedLoading();
});


export interface IAddress {
	address:string;
	postal:string;
	city:string;
	country:string;
	subpremise:string;
}

export class GoogleServiceClass extends EventEmitter{

	public constructor(){
		super();
	}

	private get tokenResponse():null | google.accounts.oauth2.TokenResponse{
		let result = localStorage.getItem("google-service-token-response");
		if (!result) {
			this._isLoggedIn = false;
			return null;
		}
		this._isLoggedIn = true;
		return JSON.parse(result);
	}

	private set tokenResponse(val:null | google.accounts.oauth2.TokenResponse){
		if (!val) {
			localStorage.removeItem("google-service-token-response");
			this.tokenExpiresAt = null;
			this._isLoggedIn = false;
			return;
		}
		localStorage.setItem("google-service-token-response", JSON.stringify(val));
		this._isLoggedIn = true;
	}

	private get tokenExpiresAt():Date | null {
		let result = localStorage.getItem("google-service-token-response-expires-at");
		if (!result) return null;
		return new Date(result);
	}

	private set tokenExpiresAt(val:null | Date) {
		if (!val){
			localStorage.removeItem("google-service-token-response-expires-at");
			return;
		}
		localStorage.setItem("google-service-token-response-expires-at", val.toJSON());
	}


	private _isLoggedIn:boolean = this.tokenResponse ? true : false;
	private client:null | google.accounts.oauth2.CodeClient = null;
	public readonly CLIENT_ID = "193257751617-ldaa1cuhr7jv9ikv6hlmjlj4dsmikcjm.apps.googleusercontent.com"

	public getAccessToken():string{
		if (!this.tokenResponse) {
			return "";
		}
		return this.tokenResponse.access_token;
	}


	public get isLoggedIn():boolean {
		return this._isLoggedIn;
	}

	public get loaded():boolean {
		return this.client != null;
	}

	async login():Promise<string> {
		if (!this.client) return "";
		this.client.requestCode();
		return new Promise<string>((resolve, reject)=>{
			this.once("login-result", (code:string, err:Error)=>{
				if (err){
					reject(err);
					return;
				}
				resolve(code);
			});
		});
	}

	public createAutocompleteAddressSession():google.maps.places.AutocompleteSessionToken | undefined {
		try{
			return new google.maps.places.AutocompleteSessionToken();
		}catch(err){
			return undefined;
		}
	}

	public async autocompletAddress(input:string, sessionToken?:google.maps.places.AutocompleteSessionToken):Promise<google.maps.places.PlacePrediction[]>{
		if (!input) return [];
		try{
			let autocompleteResult = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
				input,
				sessionToken,
				language: i18n.locale,
				includedPrimaryTypes: [
					"establishment",
					"geocode"
				]
			});
			return autocompleteResult.suggestions.map(p=>p.placePrediction).filter(p=>p!=null) as google.maps.places.PlacePrediction[];
		}catch(err){
			console.error(err);
			return [];
		}
	}

	public async autocompletAddressSuggestionToIAddress(suggestion:google.maps.places.PlacePrediction):Promise<IAddress>{
		let place = suggestion.toPlace();
		await place.fetchFields({fields: ["addressComponents"]});
		function getComponent(...types:string[]):string{
			if (!place.addressComponents) return "";
			let part = place.addressComponents.find(c => types.some(type => c.types.includes(type)));
			if (!part) return "";
			if (types.indexOf("country") != -1){
				return part.shortText || "";
			}
			return part.longText || "";
		};
		console.log(place.addressComponents);
		return {
			address: getComponent("route") + " " + getComponent("street_number"),
			city: getComponent("locality", "sublocality"),
			country: getComponent("country"),
			postal: getComponent("postal_code"),
			subpremise: getComponent("subpremise")
		} as IAddress;
	}


	logout() {
		if (!this.tokenResponse) return;
		google.accounts.oauth2.revoke(this.tokenResponse.access_token, ()=>{});
		this.tokenResponse = null;
		this.emit("auth");
	}

	async initClient() {
		await waitForGoogleLibraryLoadingToFinish();
		let ref = this;
		this.client = google.accounts.oauth2.initCodeClient({
			client_id: this.CLIENT_ID,
			scope: [
				"email",
				"profile",
				"https://www.googleapis.com/auth/gmail.send"
			].join(" "),
			ux_mode: "popup",
			callback(response:google.accounts.oauth2.CodeResponse){
				ref.emit("login-result", response.code, null);
				ref.emit("auth");
			},
			error_callback(error:google.accounts.oauth2.ClientConfigError){
				ref.emit("login-result", null, new Error(error.message));
			}
		});

		await GoogleCalendarService.initClient();

	}
};

export const GoogleService = new GoogleServiceClass();